/**
 * RobotModelManager - Handles robot model loading and visibility
 */
import { UR10Robot } from '../models/UR10Robot.js';

export class RobotModelManager {
  constructor(scene) {
    this.scene = scene;
    this.robot = null;
    this.robotModel = null;
    this.robotVisible = true;
    this.robotLoaded = false;
    this.pendingRobotType = null;
  }

  /**
   * Add robot model to the scene
   * @param {string} robotType - Robot type (ur10e, ur20, etc.)
   * @returns {Promise<void>}
   */
  async addRobotModel(robotType = 'ur10e') {
    // Remove existing robot if any
    if (this.robotModel) {
      this.scene.remove(this.robotModel);
      this.robotModel = null;
      this.robot = null;
      this.robotLoaded = false;
    }
    
    // Store the robot type
    this.pendingRobotType = robotType;
    
    this.robot = new UR10Robot(robotType);
    
    // Wait for robot to load, then set initial pose
    return new Promise((resolve) => {
      const waitForLoad = setInterval(() => {
        if (this.robot.isLoaded) {
          clearInterval(waitForLoad);
          
          this.robotModel = this.robot.getModel();
          
          // Position robot at origin
          this.robotModel.position.set(0, 0, 0);
          
          // Set visibility based on current toggle state
          this.robotModel.visible = this.robotVisible;
          
          this.scene.add(this.robotModel);
          this.robotLoaded = true;
          
          // Set initial home pose
          this.robot.setJointAngles([0, -60, -120, -90, 90, 0]);
          console.log(`${robotType.toUpperCase()} loaded and set to home pose [0, -60, -120, -90, 90, 0]`);
          
          resolve();
        }
      }, 100);
    });
  }

  /**
   * Toggle robot visibility
   */
  toggleVisibility() {
    this.robotVisible = !this.robotVisible;
    if (this.robotModel) {
      this.robotModel.visible = this.robotVisible;
    } else if (this.robotVisible && !this.robotLoaded) {
      // Load robot on first toggle if not already loaded
      const robotType = this.pendingRobotType || 'ur10e';
      this.addRobotModel(robotType);
    }
  }

  /**
   * Set robot visibility
   * @param {boolean} visible - Whether robot should be visible
   */
  setVisible(visible) {
    this.robotVisible = visible;
    if (this.robotModel) {
      this.robotModel.visible = visible;
    } else if (visible && !this.robotLoaded) {
      // Load robot on first toggle if not already loaded
      const robotType = this.pendingRobotType || 'ur10e';
      this.addRobotModel(robotType);
    }
  }

  /**
   * Set the robot type to use (stores for lazy loading)
   * @param {string} robotType - Robot type (ur10e, ur20, etc.)
   */
  setRobotType(robotType) {
    this.pendingRobotType = robotType;
    // If robot is already loaded and visible, reload with new type
    if (this.robotLoaded && this.robotVisible) {
      this.addRobotModel(robotType);
    }
  }

  /**
   * Set robot joint angles
   * @param {Array<number>} angles - Array of 6 joint angles in degrees
   */
  setJointAngles(angles) {
    if (!this.robot || !this.robotLoaded) {
      return;
    }

    if (angles.length !== 6) {
      console.error('Expected 6 joint angles, got', angles.length);
      return;
    }

    this.robot.setJointAngles(angles);
  }

  /**
   * Get current joint angles
   * @returns {Array<number>|null} Array of 6 joint angles or null if not loaded
   */
  getJointAngles() {
    if (!this.robot || !this.robotLoaded) {
      return null;
    }
    return this.robot.getJointAngles();
  }

  /**
   * Check if robot is loaded
   * @returns {boolean}
   */
  isLoaded() {
    return this.robotLoaded;
  }

  /**
   * Check if robot is visible
   * @returns {boolean}
   */
  isVisible() {
    return this.robotVisible;
  }

  /**
   * Get the robot instance (for external access)
   * @returns {UR10Robot|null}
   */
  getRobot() {
    return this.robot;
  }

  /**
   * Clean up resources
   */
  dispose() {
    if (this.robotModel) {
      this.scene.remove(this.robotModel);
      // Note: Robot model cleanup is handled by UR10Robot
      this.robotModel = null;
      this.robot = null;
      this.robotLoaded = false;
    }
  }
}
